// ============================================================
// _math_internal.js
// EV Powertrain Simulator — core 모듈 (private)
// doe.js 전용 통계·GP 헬퍼 함수 모음
//
// [Fix-⑤] math_utils.js public API에서 분리.
//   이 파일은 math_utils.js 또는 doe.js에서 직접 import한다.
//   외부 컴포넌트는 math_utils.js를 통해 접근할 것.
//
// ★ 이 파일은 다른 physics 모듈을 import하지 않는다 (circular 방지).
// ============================================================

// ── _erfApprox: Abramowitz & Stegun 근사 (최대 오차 1.5e-7) ──
/**
 * 오차 함수(Error Function) 근사 — Abramowitz & Stegun §7.1.26
 *
 * 연속 분수 근사를 이용해 erf(x)를 최대 1.5e-7 오차로 계산한다.
 * 5차 다항식 근사로 exp(-x²)와 결합해 빠른 속도를 제공한다.
 *
 * @param {number} x  - 입력 값 (실수)
 * @returns {number}  - erf(x) 근사값 ∈ (−1, 1)
 */
function _erfApprox(x) {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const poly = t * (0.254829592 +
    t * (-0.284496736 +
    t * (1.421413741 +
    t * (-1.453152027 +
    t * 1.061405429))));
  return sign * (1 - poly * Math.exp(-ax * ax));
}

// ── _normCDF: 표준정규 누적분포함수 ──
/**
 * 표준 정규 누적 분포 함수 Φ(x)
 *
 * Φ(x) = 0.5 × (1 + erf(x / √2))
 * _erfApprox()를 사용하므로 최대 오차 ≈ 1.5e-7.
 *
 * @param {number} x  - 표준 정규 변량
 * @returns {number}  - Φ(x) ∈ (0, 1)
 */
function _normCDF(x) {
  return 0.5 * (1 + _erfApprox(x / Math.SQRT2));
}

// ── _normPDF: 표준정규 확률밀도함수 ──
/**
 * 표준 정규 확률 밀도 함수 φ(x)
 *
 * φ(x) = exp(−x²/2) / √(2π)
 *
 * @param {number} x  - 표준 정규 변량
 * @returns {number}  - φ(x) ≥ 0
 */
function _normPDF(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

// ── _rbf: 방사기저함수 (RBF) 커널 — GP 회귀용 ──
// k(x, x') = sf² · exp(-0.5 · ||x-x'||² / l²)
/**
 * 제곱 지수(RBF/SE) 커널 — 가우시안 프로세스 회귀용
 *
 * k(x, x') = sf² · exp(−0.5 · ‖x − x'‖² / l²)
 *   l  : 길이 스케일 (입력 공간 상관 거리)
 *   sf : 신호 표준편차 (출력 스케일)
 *
 * @param {number|number[]} x1 - 첫 번째 입력 점
 * @param {number|number[]} x2 - 두 번째 입력 점
 * @param {number} l           - 길이 스케일
 * @param {number} sf          - 신호 표준편차
 * @returns {number}           - 커널 값 k(x1, x2) ≥ 0
 */
function _rbf(x1, x2, l, sf) {
  const diff = Array.isArray(x1)
    ? x1.reduce((s, v, i) => s + (v - x2[i]) ** 2, 0)
    : (x1 - x2) ** 2;
  return (sf * sf) * Math.exp(-0.5 * diff / (l * l));
}

// ── _matern52: Matérn 5/2 커널 [Sprint-2] ─────────────────────
// k(x,x')=sf²·(1+√5r/ℓ+5r²/3ℓ²)·exp(-√5r/ℓ) — 2회 미분, EV 효율맵 최적화에 적합
/**
 * Matérn 5/2 커널 — GP 회귀용 (RBF보다 비매끄러운 함수에 적합)
 *
 * k(x,x') = sf² · (1 + √5·r/l + 5r²/(3l²)) · exp(−√5·r/l)
 *   r = ‖x − x'‖  (유클리드 거리)
 * 2번 미분 가능(C² 연속)하므로 EV 효율맵처럼 부드럽지 않은 비선형에 적합.
 * RBF(SE)보다 지역 표현력이 높고 고주파 노이즈에 덜 민감하다.
 *
 * @param {number|number[]} x1 - 첫 번째 입력 점
 * @param {number|number[]} x2 - 두 번째 입력 점
 * @param {number} l           - 길이 스케일
 * @param {number} sf          - 신호 표준편차
 * @returns {number}           - 커널 값 k(x1, x2) ≥ 0
 */
function _matern52(x1, x2, l, sf) {
  const r2 = Array.isArray(x1)
    ? x1.reduce((s, v, i) => s + (v - x2[i]) ** 2, 0)
    : (x1 - x2) ** 2;
  const r = Math.sqrt(r2);
  const z = Math.sqrt(5) * r / Math.max(l, 1e-9);
  return (sf * sf) * (1 + z + (z * z) / 3) * Math.exp(-z);
}

// ── _matSolve: Cholesky 분해 기반 선형계 풀이 (Ax = b) ──
// GP 커널 행렬은 양정치(positive-definite)이므로 Cholesky 안전
/**
 * Cholesky 분해를 이용한 선형계 풀이 Ax = b
 *
 * 양정치(positive-definite) 행렬 A에 대해 A = L·Lᵀ (Cholesky 분해) 후
 * 전진·후진 대입으로 x를 계산한다. GP 커널 행렬은 양정치이므로 안전하게 사용할 수 있다.
 * 피벗 없음 — 수치 불안정시 L[j][j] → 1e-10으로 방어(조건: A가 양정치여야 정확).
 *
 * @param {number[][]} A - n×n 양정치 행렬
 * @param {number[]}   b - n차원 우변 벡터
 * @returns {number[]}   - 해 벡터 x (길이 n)
 */
function _matSolve(A, b) {
  const n = A.length;
  const L = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let s = A[i][j];
      for (let k = 0; k < j; k++) s -= L[i][k] * L[j][k];
      if (i === j) {
        L[i][j] = s > 0 ? Math.sqrt(s) : 1e-10;
      } else {
        L[i][j] = s / (L[j][j] || 1e-10);
      }
    }
  }
  const y = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    let s = b[i];
    for (let k = 0; k < i; k++) s -= L[i][k] * y[k];
    y[i] = s / (L[i][i] || 1e-10);
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = y[i];
    for (let k = i + 1; k < n; k++) s -= L[k][i] * x[k];
    x[i] = s / (L[i][i] || 1e-10);
  }
  return x;
}

// ── lgamma: 로그 감마 함수 (Lanczos 근사) ──
/**
 * 로그 감마 함수 ln(Γ(z)) — Lanczos 근사
 *
 * Lanczos (g=7) 계수를 사용한 근사로 수치 안정성이 높다.
 * z < 0.5 이면 반사 공식 ln(π/sin(π·z)) − lgamma(1−z) 으로 재귀 처리.
 * incompleteBeta, fDistPValue 내부에서 사용.
 *
 * @param {number} z  - 입력 값 (양의 실수, z > 0)
 * @returns {number}  - ln(Γ(z))
 */
function lgamma(z) {
  if (z < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - lgamma(1 - z);
  }
  z -= 1;
  const g = 7;
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028,
    771.32342877765313, -176.61502916214059, 12.507343278686905,
    -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
  ];
  let x = c[0];
  for (let i = 1; i < g + 2; i++) x += c[i] / (z + i);
  const t = z + g + 0.5;
  return 0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(x);
}

// ── incompleteBeta: 불완전 베타 함수 Ix(a,b) — continued fraction ──
/**
 * 정규화 불완전 베타 함수 Iₓ(a, b) — 연분수 전개 (Lentz 알고리즘)
 *
 * x > (a+1)/(a+b+2) 이면 대칭식 I₁₋ₓ(b, a)로 수렴 가속.
 * ANOVA F-검정 p값(fDistPValue)의 핵심 계산 루틴.
 * 수렴 판정: |delta − 1| < 3e-7 (최대 200회 반복).
 *
 * @param {number} x  - 적분 상한 [0, 1]
 * @param {number} a  - 베타 분포 파라미터 a > 0
 * @param {number} b  - 베타 분포 파라미터 b > 0
 * @returns {number}  - Iₓ(a, b) ∈ [0, 1]
 */
function incompleteBeta(x, a, b) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  if (x > (a + 1) / (a + b + 2)) {
    return 1 - incompleteBeta(1 - x, b, a);
  }
  const lbeta = lgamma(a) + lgamma(b) - lgamma(a + b);
  const front = Math.exp(Math.log(x) * a + Math.log(1 - x) * b - lbeta) / a;
  const MAX_ITER = 200;
  const EPS = 3e-7;
  let f = 1, C = 1, D = 1 - (a + b) * x / (a + 1);
  D = Math.abs(D) < 1e-30 ? 1e-30 : 1 / D;
  f = D;
  for (let m = 1; m <= MAX_ITER; m++) {
    let num = m * (b - m) * x / ((a + 2 * m - 1) * (a + 2 * m));
    D = 1 + num * D; D = Math.abs(D) < 1e-30 ? 1e-30 : D;
    C = 1 + num / C; C = Math.abs(C) < 1e-30 ? 1e-30 : C;
    D = 1 / D; f *= C * D;
    num = -(a + m) * (a + b + m) * x / ((a + 2 * m) * (a + 2 * m + 1));
    D = 1 + num * D; D = Math.abs(D) < 1e-30 ? 1e-30 : D;
    C = 1 + num / C; C = Math.abs(C) < 1e-30 ? 1e-30 : C;
    D = 1 / D;
    const delta = C * D;
    f *= delta;
    if (Math.abs(delta - 1) < EPS) break;
  }
  return front * f;
}

// ── fDistPValue: F분포 단측 p값 (ANOVA 유의도 검정) ──
/**
 * F분포 단측 p값 산출 — ANOVA 유의도 검정에 사용
 *
 * F통계량 F와 자유도 (df1, df2)에 대한 단측 상측 p값:
 *   p = P(X ≥ F) = Iₓ(df2/2, df1/2),  x = df2 / (df2 + df1·F)
 *
 * F ≤ 0 또는 비유한값이면 1을 반환(귀무가설 기각 불가).
 *
 * @param {number} F    - F통계량 (F > 0)
 * @param {number} df1  - 처리 자유도 (분자, 그룹 수 − 1)
 * @param {number} df2  - 오차 자유도 (분모, 총 관측 수 − 그룹 수)
 * @returns {number}    - p값 ∈ [0, 1]
 */
function fDistPValue(F, df1, df2) {
  if (!isFinite(F) || F <= 0) return 1;
  const x = df2 / (df2 + df1 * F);
  return incompleteBeta(x, df2 / 2, df1 / 2);
}

export {
  _erfApprox,
  _normCDF,
  _normPDF,
  _rbf,
  _matern52,    // [Sprint-2]
  _matSolve,
  lgamma,
  incompleteBeta,
  fDistPValue,
};